import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { productsService, type Product } from '../../services/products.service';
import { offersService } from '../../services/offers.service';
import { useWizardStore } from '../../store/wizard.store';
import api from '../../services/api';
import { useToast } from '../../components/ui/Toast';
import { LearnMoreModal } from '../../components/ui/LearnMoreModal';
import { Search, Check, Info, ArrowRight } from 'lucide-react';

const categoryLabels: Record<string, string> = {
  Mobility:   'Mobility',
  '5G':       '5G Fixed',
  Fixed_BBI:  'Fixed Broadband',
  Fixed_BDI:  'Fixed Dedicated',
};

const categoryColors: Record<string, { bg: string; text: string }> = {
  '5G':       { bg: '#EEF2FF', text: '#4F46E5' },
  Fixed_BBI:  { bg: '#E0F7FA', text: '#0097A7' },
  Fixed_BDI:  { bg: '#FFF3E0', text: '#E65100' },
  Mobility:   { bg: '#F3E8FF', text: '#7E22CE' },
};

const CATEGORY_ORDER = ['5G', 'Fixed_BBI', 'Fixed_BDI', 'Mobility'];

const sortBestsellerFirst = (list: Product[]) =>
  [...list].sort((a, b) => {
    if (a.isBestseller && !b.isBestseller) return -1;
    if (!a.isBestseller && b.isBestseller) return 1;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });

export const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setOfferId, setSelectedProduct, setStep, offerId, addItem } = useWizardStore();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState<string | null>(null);
  const [learnMoreProduct, setLearnMoreProduct] = useState<Product | null>(null);

  useEffect(() => {
    productsService.list()
      .then(setProducts)
      .catch(() => showToast('Failed to load products', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const categories = ['All', ...CATEGORY_ORDER.filter((c) => products.some((p) => p.category === c))];

  const baseFiltered = search
    ? products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : products;

  const groupedByCategory = CATEGORY_ORDER
    .filter((c) => baseFiltered.some((p) => p.category === c))
    .map((cat) => ({ cat, items: sortBestsellerFirst(baseFiltered.filter((p) => p.category === cat)) }));

  const filteredSingle = sortBestsellerFirst(baseFiltered.filter((p) => p.category === selectedCategory));

  const handleSelect = async (product: Product) => {
    setCreating(product.id);
    try {
      let id = offerId;
      let number = useWizardStore.getState().offerNumber ?? '';
      if (!id) {
        const offer = await offersService.create();
        id = offer.id; number = offer.offerNumber;
        setOfferId(id, number);
      }
      const productInfo = { id: product.id, name: product.name, category: product.category, price: product.price, vatRate: product.vatRate, requiresCoverage: product.requiresCoverage, configSchema: product.configSchema };
      if (!product.requiresCoverage || product.category === 'Mobility') {
        await api.post(`/offers/${id}/cart`, { productId: product.id, configJson: {} });
        addItem({ productId: product.id, product: productInfo });
        setStep(2);
        navigate('/offers/wizard/cart');
      } else {
        setSelectedProduct(productInfo);
        setStep(1);
        navigate('/offers/wizard/location');
      }
    } catch {
      showToast('Failed to create offer', 'error');
    } finally { setCreating(null); }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Product Catalog" subtitle="Select a product to start a new offer" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

        {/* Search + category filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
            <Search size={15} color="var(--text-3)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input type="search" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} className="inp" style={{ paddingLeft: 36 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <button key={cat} onClick={() => setSelectedCategory(cat)} className={`filter-chip${selectedCategory === cat ? ' active' : ''}`}>
                {cat === 'All' ? 'All Products' : (categoryLabels[cat] ?? cat)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ background: 'var(--surface)', borderRadius: 14, padding: 20, border: '1.5px solid var(--border)' }}>
                <div className="skeleton" style={{ height: 14, width: '40%', marginBottom: 12 }} />
                <div className="skeleton" style={{ height: 20, width: '70%', marginBottom: 20 }} />
                <div className="skeleton" style={{ height: 90, marginBottom: 16 }} />
                <div className="skeleton" style={{ height: 40 }} />
              </div>
            ))}
          </div>
        ) : selectedCategory === 'All' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            {groupedByCategory.map(({ cat, items }) => (
              <div key={cat}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: categoryColors[cat]?.bg ?? '#F3F4F6', color: categoryColors[cat]?.text ?? '#374151' }}>
                    {categoryLabels[cat] ?? cat}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{items.length} plan{items.length !== 1 ? 's' : ''}</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                  {items.map((p) => <ProductCard key={p.id} product={p} creating={creating} onSelect={handleSelect} onLearnMore={setLearnMoreProduct} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {filteredSingle.map((p) => <ProductCard key={p.id} product={p} creating={creating} onSelect={handleSelect} onLearnMore={setLearnMoreProduct} />)}
          </div>
        )}
      </main>

      {learnMoreProduct && (
        <LearnMoreModal
          isOpen
          product={learnMoreProduct}
          onClose={() => setLearnMoreProduct(null)}
          onSelect={() => {
            const p = learnMoreProduct;
            setLearnMoreProduct(null);
            if (p) handleSelect(p);
          }}
        />
      )}
    </div>
  );
};

// ── Product Card ─────────────────────────────────────────────────────────────
const ProductCard: React.FC<{
  product: Product;
  creating: string | null;
  onSelect: (p: Product) => void;
  onLearnMore: (p: Product) => void;
}> = ({ product, creating, onSelect, onLearnMore }) => {
  const [hovered, setHovered] = useState(false);

  const features = product.description
    ? product.description.split(/[;\n]/).map((f) => f.trim()).filter(Boolean)
    : [];

  const isPopular = Boolean(product.isBestseller);
  const catColor = categoryColors[product.category] ?? { bg: '#F3F4F6', text: '#374151' };
  const price = Number(product.price) || 0;
  const vatRate = Number(product.vatRate) || 0.15;
  const priceExclVat = price / (1 + vatRate);
  const vatAmount = price - priceExclVat;

  // Extract contract months
  const contractMonths = product.contractTerms
    ? (product.contractTerms.match(/(\d+)-month/i)?.[1] ?? null)
    : null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--surface)',
        border: `1.5px solid ${hovered ? 'var(--p)' : 'var(--border)'}`,
        borderRadius: 14,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all .18s',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? '0 4px 20px rgba(0,56,49,.12)' : 'none',
      }}
    >
      {/* Popular badge */}
      {isPopular && (
        <div style={{ position: 'absolute', top: -1, right: 16, background: 'var(--p)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: '0 0 8px 8px', letterSpacing: '0.02em' }}>
          Popular
        </div>
      )}

      {/* Category + code row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, paddingTop: isPopular ? 8 : 0 }}>
        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, background: catColor.bg, color: catColor.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {categoryLabels[product.category] ?? product.category}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>{product.id.split('-')[0]?.toUpperCase()}</span>
      </div>

      {/* Name + tagline */}
      <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)', marginBottom: 2 }}>{product.name}</div>
      {features[0] && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>{features[0]}</div>}

      {/* Features list */}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {features.slice(1, 5).map((feat, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-2)' }}>
            <Check size={13} color="var(--succ)" style={{ flexShrink: 0, marginTop: 2 }} />
            {feat}
          </li>
        ))}
      </ul>

      {/* Pricing box */}
      <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: 12, marginTop: 'auto' }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-1)', lineHeight: 1 }}>
          SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(priceExclVat)}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3 }}>
          +VAT SAR {vatAmount.toFixed(2)} / month
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--p)', background: 'var(--p-light)', padding: '2px 7px', borderRadius: 20 }}>Monthly billing</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', background: 'var(--surface)', border: '1px solid var(--border)', padding: '2px 7px', borderRadius: 20 }}>
            {contractMonths ? `${contractMonths} mo min.` : '12 mo min.'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={(e) => { e.stopPropagation(); onLearnMore(product); }}
          style={{ flex: 1, padding: '8px 12px', background: 'transparent', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all .15s' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--p)'; (e.currentTarget as HTMLElement).style.color = 'var(--p)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-2)'; }}
        >
          <Info size={13} /> Learn more
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(product); }}
          disabled={creating === product.id}
          style={{ flex: 1, padding: '8px 12px', background: 'var(--p)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: creating === product.id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, opacity: creating === product.id ? 0.7 : 1, transition: 'all .15s' }}
          onMouseEnter={(e) => { if (creating !== product.id) (e.currentTarget as HTMLElement).style.background = 'var(--p-hover)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--p)'; }}
        >
          <ArrowRight size={13} />
          {creating === product.id ? 'Creating…' : 'Select'}
        </button>
      </div>
    </div>
  );
};
